import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import SEO from '../components/SEO';
import AdminLoginGate from '../components/AdminLoginGate';
import AdminTabs from '../components/AdminTabs';
import { Input } from '../components/ui/input';
import { API, authHeaders, useAdminAuth } from '../lib/adminAuth';

// The QR must encode the FRONTEND site URL (where customers scan to) — that's
// `window.location.origin` (e.g. https://www.glossoproundtable.org), NOT
// REACT_APP_BACKEND_URL which on Emergent deployments points at the backend
// preview host. The QR PNG is generated by the backend (so the /api/qr call
// uses the backend host), but the URL it encodes is the public frontend one.
const BACKEND = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
function buildPublicUrl() {
  if (typeof window === 'undefined') return '';
  return `${window.location.origin}/sweet-jar`;
}
function buildQrUrl() {
  const publicUrl = buildPublicUrl();
  return `${BACKEND}/api/qr?url=${encodeURIComponent(publicUrl)}&with_logo=true&size=800`;
}

function StatPill({ label, value, tone = 'default' }) {
  const palette = tone === 'amber'
    ? 'bg-[#FEF7E6] text-[#B97400]'
    : tone === 'red'
    ? 'bg-red-50 text-red-700'
    : 'bg-zinc-100 text-[#0A0A0A]';
  return (
    <div className={`px-4 py-3 ${palette}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">{label}</div>
      <div className="text-2xl font-bold mt-0.5">{value}</div>
    </div>
  );
}

function Row({ row, onDelete, isSuperadmin }) {
  return (
    <tr className="border-b border-zinc-200 hover:bg-zinc-50" data-testid={`sweet-jar-row-${row.id}`}>
      <td className="px-3 py-2.5 text-xs text-[#52525B] whitespace-nowrap">
        {new Date(row.submitted_at).toLocaleString('en-GB', {
          day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
        })}
      </td>
      <td className="px-3 py-2.5 text-sm font-semibold text-[#0A0A0A]">{row.name}</td>
      <td className="px-3 py-2.5 text-sm text-[#52525B]">
        <span className={row.duplicate_email ? 'text-red-700 font-semibold' : ''}>{row.email}</span>
        {row.duplicate_email && (
          <span className="ml-2 inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-wider bg-red-100 text-red-700 font-bold">DUP</span>
        )}
      </td>
      <td className="px-3 py-2.5 text-sm text-[#52525B]">
        {row.phone || <span className="text-zinc-400">—</span>}
        {row.duplicate_phone && (
          <span className="ml-2 inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-wider bg-red-100 text-red-700 font-bold">DUP</span>
        )}
      </td>
      <td className="px-3 py-2.5 text-base font-bold text-[#F0A500] text-right">{row.guess}</td>
      {isSuperadmin && (
        <td className="px-3 py-2.5 text-right">
          <button
            type="button"
            onClick={() => onDelete(row)}
            data-testid={`sweet-jar-row-${row.id}-delete`}
            className="text-[10px] uppercase tracking-wider font-bold text-red-600 hover:text-red-800"
          >
            Delete
          </button>
        </td>
      )}
    </tr>
  );
}

function Dashboard({ token, me, onLogout }) {
  const [data, setData] = useState({ guesses: [], total: 0, closed: false, closes_at: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [winningCount, setWinningCount] = useState('');
  // Recomputed at render time so the URLs reflect whichever host the admin
  // is browsing from (preview vs production), not the build-time backend env.
  const publicUrl = useMemo(() => buildPublicUrl(), []);
  const qrUrl = useMemo(() => buildQrUrl(), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: payload } = await axios.get(`${API}/admin/sweet-jar/guesses`, { headers: authHeaders(token) });
      setData(payload);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else setError('Failed to load guesses.');
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete ${row.name}'s guess of ${row.guess}?`)) return;
    try {
      await axios.delete(`${API}/admin/sweet-jar/guesses/${row.id}`, { headers: authHeaders(token) });
      load();
    } catch {
      setError('Failed to delete that entry.');
    }
  };

  const handleDownloadCsv = async () => {
    try {
      const response = await axios.get(`${API}/admin/sweet-jar/guesses.csv`, {
        headers: authHeaders(token), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      const cd = response.headers['content-disposition'] || '';
      const match = cd.match(/filename=([^;]+)/);
      link.setAttribute('download', match ? match[1].trim() : 'sweet-jar-guesses.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download CSV.');
    }
  };

  const handleDownloadQr = async () => {
    // Cross-origin `download` attributes are ignored by browsers, so the
    // link opens in a new tab instead of saving. Fetch as a blob first,
    // then stream it into a same-origin object URL so the download works.
    try {
      const response = await axios.get(qrUrl, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'image/png' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'sweet-jar-qr.png';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Could not download the QR code. Try right-click → Save image on the preview above.');
    }
  };

  const filtered = useMemo(() => {
    if (!query) return data.guesses;
    const q = query.toLowerCase();
    return data.guesses.filter((r) =>
      r.name?.toLowerCase().includes(q) ||
      r.email?.toLowerCase().includes(q) ||
      String(r.guess).includes(q) ||
      (r.phone || '').toLowerCase().includes(q),
    );
  }, [data.guesses, query]);

  // Winner calculator: nearest guess to the entered actual count (ties = all tied).
  const winners = useMemo(() => {
    const target = Number(winningCount);
    if (!winningCount || !Number.isFinite(target) || data.guesses.length === 0) return null;
    let bestDelta = Infinity;
    const scored = data.guesses.map((g) => {
      const delta = Math.abs(g.guess - target);
      if (delta < bestDelta) bestDelta = delta;
      return { ...g, delta };
    });
    return scored
      .filter((g) => g.delta === bestDelta)
      .sort((a, b) => new Date(a.submitted_at) - new Date(b.submitted_at));
  }, [data.guesses, winningCount]);

  const dupEmails = data.guesses.filter((g) => g.duplicate_email).length;

  const isSuperadmin = me.role === 'superadmin';

  return (
    <div className="bg-[#F4F4F5] min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="admin-sweet-jar-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#F0A500] block mb-2">Admin Dashboard</span>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#0A0A0A]">Sweet Jar &mdash; Heritage Weekend</h1>
            <p className="text-sm text-[#52525B] mt-2">
              Hidden competition page &mdash; only reachable via the printed QR code.{' '}
              {data.closed
                ? <strong className="text-red-700">Competition closed</strong>
                : <>Closes {data.closes_at ? new Date(data.closes_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Sun 31 May, 17:00'}</>
              }
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button type="button" onClick={load} data-testid="sweet-jar-refresh-btn" className="px-4 py-2 bg-zinc-200 hover:bg-zinc-300 text-[#0A0A0A] font-semibold text-sm">Refresh</button>
            <button type="button" onClick={handleDownloadCsv} disabled={data.guesses.length === 0} data-testid="sweet-jar-csv-btn" className="px-4 py-2 bg-[#F0A500] hover:bg-[#d99400] disabled:opacity-50 text-[#0A0A0A] font-bold uppercase tracking-wider text-xs">Download CSV</button>
          </div>
        </div>

        {/* QR + stats split */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-[#0A0A0A] text-white p-6 border-l-4 border-[#F0A500] flex flex-col items-start" data-testid="sweet-jar-qr-card">
            <div className="text-xs uppercase tracking-widest font-bold text-[#F0A500] mb-3">Print this QR code</div>
            <img
              src={qrUrl}
              alt="QR code linking to the sweet jar competition page"
              className="w-44 h-44 bg-white p-2"
              data-testid="sweet-jar-qr-image"
            />
            <p className="text-xs text-zinc-400 mt-3 leading-relaxed">
              Points to <span className="text-zinc-200 break-all">{publicUrl}</span>
            </p>
            <button
              type="button"
              onClick={handleDownloadQr}
              data-testid="sweet-jar-qr-download-btn"
              className="mt-4 px-4 py-2 bg-[#F0A500] hover:bg-[#d99400] text-[#0A0A0A] font-bold uppercase tracking-wider text-xs"
            >
              ↓ Download QR (PNG)
            </button>
          </div>

          <div className="lg:col-span-2 grid grid-cols-2 gap-3 content-start">
            <StatPill label="Total guesses" value={data.total} />
            <StatPill label="Duplicate emails" value={dupEmails} tone={dupEmails > 0 ? 'red' : 'default'} />
            <StatPill
              label="Highest guess"
              value={data.guesses.length ? Math.max(...data.guesses.map((g) => g.guess)) : '—'}
              tone="amber"
            />
            <StatPill
              label="Lowest guess"
              value={data.guesses.length ? Math.min(...data.guesses.map((g) => g.guess)) : '—'}
              tone="amber"
            />

            {/* Winner finder */}
            <div className="col-span-2 bg-white p-5 border border-zinc-200 mt-2" data-testid="sweet-jar-winner-finder">
              <Label className="text-xs uppercase tracking-widest font-bold text-[#52525B] mb-2 block">
                Find the winner &mdash; enter the actual sweet count
              </Label>
              <Input
                type="number"
                inputMode="numeric"
                placeholder="e.g. 412"
                value={winningCount}
                onChange={(e) => setWinningCount(e.target.value)}
                data-testid="sweet-jar-winner-input"
              />
              {winners && winners.length > 0 && (
                <div className="mt-4 p-3 bg-[#FEF7E6] border-l-4 border-[#F0A500] text-sm" data-testid="sweet-jar-winner-result">
                  <div className="text-xs uppercase tracking-widest font-bold text-[#F0A500] mb-2">
                    {winners.length === 1 ? 'Winner' : `Tied — ${winners.length} entries`}
                  </div>
                  {winners.map((w) => (
                    <div key={w.id} className="py-1">
                      <strong className="text-[#0A0A0A]">{w.name}</strong> guessed{' '}
                      <strong className="text-[#F0A500]">{w.guess}</strong>{' '}
                      <span className="text-[#71717A]">
                        ({w.delta === 0 ? 'exact match!' : `${w.delta} off`})
                      </span>
                      <span className="text-xs text-[#52525B] block">{w.email} · {w.phone || 'no phone'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Search + table */}
        <div className="bg-white border border-zinc-200 p-4 mb-4">
          <Input
            placeholder="Search by name, email, phone, or guess…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="sweet-jar-search"
          />
        </div>

        {error && <p data-testid="sweet-jar-error" className="text-sm text-red-600 mb-4">{error}</p>}

        {loading ? (
          <p className="text-sm text-[#52525B]">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-[#52525B] italic" data-testid="sweet-jar-empty">
            {data.guesses.length === 0 ? 'No guesses yet — get that QR code out there!' : 'No matches for your search.'}
          </p>
        ) : (
          <div className="bg-white border border-zinc-200 overflow-x-auto">
            <table className="w-full text-left" data-testid="sweet-jar-table">
              <thead className="bg-zinc-100">
                <tr className="text-[10px] uppercase tracking-widest font-bold text-[#52525B]">
                  <th className="px-3 py-2">Submitted</th>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Phone</th>
                  <th className="px-3 py-2 text-right">Guess</th>
                  {isSuperadmin && <th className="px-3 py-2 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <Row key={row.id} row={row} onDelete={handleDelete} isSuperadmin={isSuperadmin} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// Local Label wrapper to avoid pulling Radix Label just for a class.
function Label({ className, children, ...rest }) {
  return <label className={className} {...rest}>{children}</label>;
}

export default function AdminSweetJarPage() {
  const { token, me, checking, loginSuccess, logout } = useAdminAuth();
  if (checking) return <div className="min-h-[70vh] flex items-center justify-center text-zinc-500">Checking session…</div>;
  if (!token || !me) {
    return (
      <div data-testid="admin-sweet-jar-page">
        <SEO title="Admin Sign-in" description="Restricted admin area." path="/admin/sweet-jar" noIndex />
        <AdminLoginGate title="Sweet Jar Admin" subtitle="Sign in to view competition entries." onLoggedIn={loginSuccess} />
      </div>
    );
  }
  return (
    <div data-testid="admin-sweet-jar-page">
      <SEO title="Admin — Sweet Jar Competition" description="Restricted admin area." path="/admin/sweet-jar" noIndex />
      <AdminTabs current="sweet-jar" me={me} onLogout={logout} />
      <Dashboard token={token} me={me} onLogout={logout} />
    </div>
  );
}
